/**
 * ---------------------------------------------------------------------------------------------------------------------
 * PRODUCT CART SYNC
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Keeps the product page's add to cart controls in step with the cart: the inline and sticky buttons, the quantity
 * selectors, the "view cart" state, and the cart drawer's line for this product. It also watches cart mutations made
 * anywhere else on the page so those controls follow along.
 *
 * Configuration is read from the `script[data-ab-cart-sync]` JSON block that `sections/main-product.liquid` renders,
 * so this file stays static and cacheable. One instance is booted per config block, and again on
 * `shopify:section:load` so the theme editor re-binds after a section re-render.
 */
(function () {
  'use strict';

  function boot(config) {
    var SECTION_ID = String(config.sectionId);
    var ENABLE_STICKY_ATC = config.enableStickyAtc === true;
    var PRODUCT_ID = config.productId;
    var VIEW_CART_LABEL = config.viewCartLabel || 'View Cart';
    var currentVariantId = null;
    // Read server-rendered cart state so JS variable matches the HTML on first paint.
    // This prevents the ATC button from flashing in when the product is already in cart.
    var isInCart = (function () {
      var inline = document.getElementById('ab-inline-atc-' + SECTION_ID);
      if (inline && inline.getAttribute('data-in-cart') === 'true') return true;
      if (!ENABLE_STICKY_ATC) return false;
      var bar = document.getElementById('ab-bottom-sticky-atc-' + SECTION_ID);
      return bar ? bar.getAttribute('data-in-cart') === 'true' : false;
    })();
    var isSyncing = false; // true while WE write to the cart (prevents feedback loops)
    var lastQtyPushAt = 0;

    function root() { return document.getElementById('shopify-section-' + SECTION_ID); }

    function getCurrentVariantId() {
      var el = root();
      if (!el) return null;
      var inputs = el.querySelectorAll('input[name="id"]');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].disabled) continue;
        var v = parseInt(inputs[i].value, 10);
        if (!isNaN(v)) return v;
      }
      for (var j = 0; j < inputs.length; j++) {
        var fallback = parseInt(inputs[j].value, 10);
        if (!isNaN(fallback)) return fallback;
      }
      return null;
    }

    function getVariantIdCandidates() {
      var ids = [];
      function add(id) {
        id = parseInt(id, 10);
        if (!isNaN(id) && ids.indexOf(id) === -1) ids.push(id);
      }
      add(getCurrentVariantId());
      add(currentVariantId);
      var bar = getStickyBar();
      if (bar) {
        var select = bar.querySelector('[data-ab-sticky-variant-select]');
        if (select && select.value) add(select.value);
      }
      return ids;
    }

    function findCartItem(cart, variantIds) {
      if (!cart || !Array.isArray(cart.items)) return null;
      var item = null;
      (variantIds || []).forEach(function (variantId) {
        if (item) return;
        (cart.items || []).forEach(function (it) {
          if (!item && String(it.variant_id) === String(variantId)) item = it;
        });
      });
      return item;
    }

    function getQtyWrap(input) {
      if (!input) return null;
      return input.closest('quantity-input') || input.closest('.quantity') || input.parentElement;
    }

    function updateQtyVisibility(inCart) {
      var el = root();
      if (!el) return;
      el.querySelectorAll('buy-buttons').forEach(function (bb) {
        if (bb.closest('product-quick-add')) return;
        bb.classList.toggle('ab-cart-qty-hidden', !inCart);
        bb.querySelectorAll('input[name="quantity"]').forEach(function (input) {
          var wrap = getQtyWrap(input);
          if (wrap) wrap.classList.toggle('ab-qty-hidden', !inCart);
        });
      });
      el.querySelectorAll('.product-info__quantity-selector').forEach(function (node) {
        if (node.closest('product-quick-add, [data-ab-sticky-qty]')) return;
        node.classList.toggle('ab-qty-hidden', !inCart);
        if (inCart) node.style.removeProperty('display');
      });
    }

    function updateMainButtonStates(inCart) {
      updateQtyVisibility(inCart);
    }

    function isStickyViewCartVisible() {
      var bar = getStickyBar();
      if (!bar) return false;
      var viewCartBtn = bar.querySelector('[data-ab-sticky-view-cart]:not(.ab-vc-hidden)');
      if (!viewCartBtn) return false;
      var style = window.getComputedStyle(viewCartBtn);
      return style.display !== 'none' && style.visibility !== 'hidden' && viewCartBtn.offsetParent !== null;
    }

    function getStickyBar() {
      return document.getElementById('ab-bottom-sticky-atc-' + SECTION_ID);
    }

    function getInlineAtc() {
      return document.getElementById('ab-inline-atc-' + SECTION_ID);
    }

    function getAllAtcButtons() {
      var btns = [];
      var bar = getStickyBar();
      if (bar) {
        var stickyBtn = bar.querySelector('[data-ab-sticky-atc]');
        if (stickyBtn) btns.push(stickyBtn);
      }
      var inline = getInlineAtc();
      if (inline) {
        var inlineBtn = inline.querySelector('[data-ab-inline-atc-btn]');
        if (inlineBtn) btns.push(inlineBtn);
      }
      return btns;
    }

    function setAtcLoading(on) {
      getAllAtcButtons().forEach(function (btn) {
        if (!btn) return;
        if (on) {
          // Still show loader on the other ATC even if this one is sold-out/disabled
          if (btn.disabled && btn.classList.contains('ab-atc-hidden')) return;
          btn.classList.add('is-loading');
          btn.setAttribute('aria-busy', 'true');
        } else {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
        }
      });
      if (on) {
        clearTimeout(setAtcLoading._safety);
        setAtcLoading._safety = setTimeout(function () {
          setAtcLoading(false);
        }, 10000);
      } else {
        clearTimeout(setAtcLoading._safety);
      }
    }

    function updateStickyButtonStates(showViewCart) {
      var bar = getStickyBar();
      if (!bar) return;
      var atc = bar.querySelector('[data-ab-sticky-atc]');
      var viewCart = bar.querySelector('[data-ab-sticky-view-cart]');
      var qty = bar.querySelector('[data-ab-sticky-qty]');
      if (atc) atc.classList.toggle('ab-atc-hidden', showViewCart);
      if (viewCart) viewCart.classList.toggle('ab-vc-hidden', !showViewCart);
      if (qty) qty.classList.toggle('ab-sticky-qty-hidden', !showViewCart);
      bar.classList.toggle('ab-bottom-sticky-atc--in-cart', showViewCart);
    }

    function getInlineQtyInput() {
      var wrap = getInlineAtc();
      return wrap ? wrap.querySelector('[data-ab-inline-qty] input[name="quantity"]') : null;
    }

    function updateInlineAtcStates(inCart) {
      var wrap = getInlineAtc();
      if (!wrap) return; // A/B: sticky-only — no-op when buy-buttons-new is absent
      var atc = wrap.querySelector('[data-ab-inline-atc-btn]');
      var qty = wrap.querySelector('[data-ab-inline-qty]');
      if (atc) atc.classList.toggle('ab-atc-hidden', inCart);
      if (qty) qty.classList.toggle('ab-inline-qty-hidden', !inCart);
      wrap.classList.toggle('ab-inline-atc--in-cart', inCart);
      wrap.setAttribute('data-in-cart', inCart ? 'true' : 'false');
    }

    function updateButtonStates(inCart) {
      isInCart = inCart;
      if (ENABLE_STICKY_ATC) {
        updateStickyButtonStates(inCart);
      } else {
        updateMainButtonStates(inCart);
      }
      // Optional inline ATC (A/B). Safe no-op when the block is not on the page.
      updateInlineAtcStates(inCart);
      setAtcLoading(false);
    }

    function getStickyQtyInput() {
      var bar = getStickyBar();
      return bar ? bar.querySelector('[data-ab-sticky-qty] input[name="quantity"]') : null;
    }

    function getTrackedVariantId() {
      var mainId = getCurrentVariantId();
      if (mainId) return mainId;
      if (currentVariantId) return currentVariantId;
      var bar = getStickyBar();
      if (bar) {
        var select = bar.querySelector('[data-ab-sticky-variant-select]');
        if (select && select.value) {
          var selected = parseInt(select.value, 10);
          if (!isNaN(selected)) return selected;
        }
      }
      return null;
    }

    function isStickyQtyVisible() {
      return isStickyViewCartVisible();
    }

    function getActiveQtyInput() {
      if (ENABLE_STICKY_ATC && isStickyViewCartVisible()) {
        return getStickyQtyInput();
      }
      return getMainQtyInput();
    }

    // ---- PDP quantity input ----------------------------------------------
    function getAllPdpQtyInputs() {
      var el = root();
      if (!el) return [];
      var inputs = [];
      el.querySelectorAll('input[name="quantity"]').forEach(function (input) {
        if (input.closest('product-quick-add')) return;
        if (input.closest('cart-drawer, #cart-drawer, .cart-drawer')) return;
        inputs.push(input);
      });
      return inputs;
    }

    function getMainQtyInput() {
      var inputs = getAllPdpQtyInputs();
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].closest('[data-ab-sticky-qty]')) continue;
        return inputs[i];
      }
      return inputs[0] || null;
    }

    function setQtyInputValue(input, qty, silent) {
      if (!input) return;
      var strQty = String(qty);
      if (input.value === strQty) return;
      input.value = strQty;
      var inDrawer = !!input.closest('cart-drawer, #cart-drawer, .cart-drawer');
      if (inDrawer) {
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      if (silent) return;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function getLineItemVariantId(line) {
      if (!line) return null;
      var vid = line.getAttribute('data-variant-id') || (line.dataset && line.dataset.variantId);
      if (vid) return String(vid);
      var idInput = line.querySelector('input[name="id"], [data-variant-id]');
      if (idInput) {
        vid = idInput.value || idInput.getAttribute('data-variant-id') || (idInput.dataset && idInput.dataset.variantId);
        if (vid) return String(vid);
      }
      return null;
    }

    function updateDrawerQtyInputs(inputs, qty) {
      inputs.forEach(function (input) {
        setQtyInputValue(input, qty, true);
      });
    }

    function syncCartDrawerLineQty(qty, variantId) {
      var variantIds = [];
      function add(id) {
        id = parseInt(id, 10);
        if (!isNaN(id) && variantIds.indexOf(id) === -1) variantIds.push(id);
      }
      add(variantId);
      getVariantIdCandidates().forEach(add);
      if (!variantIds.length) return;

      var matchedInputs = [];

      document.querySelectorAll(
        'cart-drawer line-item, cart-drawer .line-item, #cart-drawer .line-item, .cart-drawer .line-item, cart-drawer .ab-cart-item-wrap, .cart-drawer .ab-cart-item-wrap'
      ).forEach(function (line) {
        var lineVid = getLineItemVariantId(line);
        if (!lineVid || variantIds.indexOf(parseInt(lineVid, 10)) === -1) return;
        line.querySelectorAll('input[name="quantity"], .quantity-selector__input').forEach(function (input) {
          matchedInputs.push(input);
        });
      });

      // Local write only. There used to be a `/cart.js` fallback here that re-queried the drawer after a round trip.
      // It could never find anything: this theme renders the drawer quantity box as
      // `<input id="line-item-qty" class="quantity-input" data-line-key="...">` (snippets/line-item.liquid) — it has
      // no `name` attribute and no `.quantity-selector__input` class, so neither selector above ever matched. The only
      // thing the fallback did was fire an uncached GET on every call, and finishCartResponse called this four times
      // per quantity change. Those eight-plus requests and their DOM callbacks are what kept <body> mutating long
      // enough to starve GoKwik's 700ms quiet-window debounce. The drawer is re-rendered from section markup anyway.
      if (matchedInputs.length) updateDrawerQtyInputs(matchedInputs, qty);
    }

    function updateStickyQtyDecreaseIcon(qty) {
      var n = parseInt(qty, 10);
      if (ENABLE_STICKY_ATC) {
        var bar = getStickyBar();
        if (bar) {
          var stickyQty = bar.querySelector('[data-ab-sticky-qty]');
          if (stickyQty) {
            if (n <= 1) stickyQty.setAttribute('data-qty-is-one', '');
            else stickyQty.removeAttribute('data-qty-is-one');
          }
        }
      }
      var inline = getInlineAtc();
      if (inline) {
        var inlineQty = inline.querySelector('[data-ab-inline-qty]');
        if (inlineQty) {
          if (n <= 1) inlineQty.setAttribute('data-qty-is-one', '');
          else inlineQty.removeAttribute('data-qty-is-one');
        }
      }
    }

    /**
     * `syncDrawer` must be false whenever this product is NOT in the cart. The drawer line is still on screen for a
     * moment after a removal, and writing a quantity into it put the line back to 1 — the removal looked like it had
     * been ignored and the shopper had to remove it a second time.
     */
    function setPdpQty(qty, syncDrawer) {
      getAllPdpQtyInputs().forEach(function (input) {
        setQtyInputValue(input, qty, true);
      });
      updateStickyQtyDecreaseIcon(qty);

      if (syncDrawer === false) return;

      syncCartDrawerLineQty(qty, currentVariantId || getTrackedVariantId() || getCurrentVariantId());
    }

    function applyCartState(cart, opts) {
      opts = opts || {};
      if (!cart || !root()) return;

      // Write cart to sessionStorage so the collection page can apply it
      // immediately on bfcache restore (back/forward navigation) without waiting
      // for a network round-trip. Key must match AB_CART_KEY in ab-product-card.js.
      if (Array.isArray(cart.items)) {
        try {
          sessionStorage.setItem('ab_pc_cart', JSON.stringify({
            items: cart.items.map(function (it) {
              return { product_id: it.product_id, variant_id: it.variant_id, quantity: it.quantity };
            }),
            item_count: cart.item_count || 0
          }));
        } catch (e) {}
      }

      if (!opts.force && isSyncing) return;
      if (!opts.force && isInCart && Date.now() - lastQtyPushAt < 600) return;

      var variantIds = getVariantIdCandidates();
      var trackedVariantId = getTrackedVariantId() || variantIds[0];
      if (!trackedVariantId) return;
      currentVariantId = trackedVariantId;

      var item = findCartItem(cart, variantIds);
      var nextInCart = !!item;
      if (!nextInCart && opts.keepInCart) nextInCart = true;

      updateButtonStates(nextInCart);
      wireStickyViewCartButton();
      setPdpQty(item ? item.quantity : 1, !!item);
      if (cart.item_count != null) {
        syncHeaderCartCount(cart);
      }
    }

    function updateStickyBagCount(count) {
      var bar = getStickyBar();
      if (!bar) return;
      var badge = bar.querySelector('[data-ab-sticky-bag-count]');
      if (!badge) return;
      var n = parseInt(count, 10) || 0;
      badge.textContent = n > 99 ? '99+' : (n > 0 ? String(n) : '');
      badge.setAttribute('data-count', String(n));
    }

    // ---- Cart drawer refresh via Section Rendering API -------------------
    function getCartSectionIds() {
      var ids = [];
      function add(id) { if (id && ids.indexOf(id) === -1) ids.push(id); }
      document.querySelectorAll(
        'cart-drawer, #cart-drawer, .cart-drawer, cart-notification, #cart-notification, #cart-icon-bubble, [id*="cart-drawer"], .header__cart-count, header cart-count'
      ).forEach(function (node) {
        var sec = node.closest('[id^="shopify-section-"]');
        if (sec) add(sec.id.replace('shopify-section-', ''));
      });
      if (!ids.length) {
        document.querySelectorAll('[id^="shopify-section-"]').forEach(function (sec) {
          if (sec.querySelector('cart-drawer, #cart-drawer, .cart-drawer, .header__cart-count, cart-count')) {
            add(sec.id.replace('shopify-section-', ''));
          }
        });
      }
      return ids;
    }

    function refreshCartDrawerMarkup() {
      var ids = getCartSectionIds();
      if (!ids.length) return Promise.resolve();
      var rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      return fetch(rootUrl + '?sections=' + ids.join(','), { cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          applyCartSections(sections);
        })
        .catch(function () {});
    }

    /**
     * Replacing a section's markup destroys the nodes third-party scripts bound to. GoKwik renders its checkout
     * buttons disabled and only re-enables them from a MutationObserver that disconnects itself while it works, so a
     * swap landing in that gap left the button stuck showing its loading state with nothing left to re-enable it.
     * Re-enabling them here does exactly what GoKwik's own handler does, and the section event lets anything else
     * re-bind the way it would after a normal Shopify section render.
     */
    function announceSectionReplaced(target, id) {
      try {
        target.querySelectorAll('.gokwik-checkout button, #gokwik-buy-now').forEach(function (button) {
          button.disabled = false;
          button.classList.remove('disabled');
          button.removeAttribute('disabled');
        });
      } catch (e) {}

      try {
        target.dispatchEvent(new CustomEvent('shopify:section:load', {
          bubbles: true,
          detail: { sectionId: id }
        }));
      } catch (e) {}
    }

    function applyCartSections(sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (id) {
        var markup = sections[id];
        if (markup == null) return;
        var target = document.getElementById('shopify-section-' + id);
        if (!target) return;
        var doc = new DOMParser().parseFromString(markup, 'text/html');
        var freshSection = doc.getElementById('shopify-section-' + id) || doc.body;
        // Preserve the live <cart-drawer> element (and its open state); swap inner only
        var liveDrawer = target.querySelector('cart-drawer');
        var freshDrawer = freshSection.querySelector('cart-drawer');
        if (liveDrawer && freshDrawer) {
          liveDrawer.innerHTML = freshDrawer.innerHTML;
          announceSectionReplaced(target, id);
          return;
        }
        // Header: only swap the cart count bubble so we don't reset the whole header
        var liveHeaderCount = target.querySelector('.header__cart-count');
        var freshHeaderCount = freshSection.querySelector('.header__cart-count');
        if (liveHeaderCount && freshHeaderCount) {
          liveHeaderCount.innerHTML = freshHeaderCount.innerHTML;
          liveHeaderCount.className = freshHeaderCount.className;
          return;
        }
        target.innerHTML = freshSection.innerHTML;
        announceSectionReplaced(target, id);
      });
    }

    function updateCartCount(count) {
      if (count == null) return;
      count = parseInt(count, 10);
      if (isNaN(count)) return;

      function applyCount(el) {
        if (!el) return;

        // Go through the theme's own `itemCount` setter on <cart-count>. Writing textContent directly — as this used
        // to — silently broke the bottom navigation bar's badge, because that setter opens with:
        //
        //     set itemCount(count) { if (this.itemCount === count) return; ... }
        //     get itemCount()      { return parseInt(this.innerText); }
        //
        // Having already written the number meant the theme found it unchanged and returned early. Its animation is
        // the ONLY thing that puts `opacity: 1` inline on the badge, and blocks/bottom-nav-item.liquid renders that
        // badge with `bottom-nav__badge--empty` (a flat `opacity: 0`) whenever the cart was empty at page load. So
        // the count updated but stayed invisible — the "cart count sometimes missing" report. Whether it happened
        // depended on which script wrote first, which is why it was intermittent.
        //
        // assets/ab-product-card.js has always used the setter, which is why the home and collection pages were fine.
        if (typeof el.itemCount !== 'undefined') {
          el.itemCount = count;
        } else {
          el.textContent = count;
        }

        el.classList.toggle('opacity-0', count === 0);
        el.classList.toggle('hidden', count === 0);
        el.classList.toggle('visually-hidden', count === 0);

        // Server-rendered onto the bar's badge when the cart was empty; nothing in the theme ever clears it, so it
        // has to be cleared here or the badge stays at opacity 0 for the rest of the session.
        if (el.classList.contains('bottom-nav__badge')) {
          el.classList.toggle('bottom-nav__badge--empty', count === 0);
        }
      }

      document.querySelectorAll('.cart-count, [data-cart-count], cart-count, .header__cart-count cart-count').forEach(applyCount);

      document.querySelectorAll('.header__cart-count').forEach(function (wrap) {
        wrap.classList.toggle('opacity-0', count === 0);
        applyCount(wrap.querySelector('cart-count, .count-bubble'));
      });
    }

    function persistCartCount(count) {
      try {
        sessionStorage.setItem('cart-count', JSON.stringify({
          value: String(count),
          timestamp: Date.now()
        }));
      } catch (e) {}
    }

    function syncHeaderCartCount(cart) {
      if (!cart || cart.item_count == null) return;
      updateCartCount(cart.item_count);
      updateStickyBagCount(cart.item_count);
      persistCartCount(cart.item_count);
    }

    /**
     * The theme's CartDrawer._onCartChanged (theme.js.liquid:1782) opens with, and does not guard:
     *
     *     new DOMParser().parseFromString(event.detail.cart["sections"][extractSectionId(this)], "text/html")
     *
     * A `cart:change` carrying a cart with no `sections` key therefore throws TypeError on its very first statement.
     * It is an async listener, so the throw becomes a silent unhandled rejection — and everything after it is skipped:
     * the drawer is never re-rendered, and the `cart:refresh` it would have dispatched at the end never fires.
     *
     * Our cart objects come from `/cart.js`, which never returns `sections`; we only bolt them on when
     * `/cart/change.js` echoed them back. So this must be checked before dispatching, or a removal simply hangs.
     */
    function cartCanRenderDrawer(cart) {
      if (!cart || !cart.sections || typeof cart.sections !== 'object') return false;

      var drawer = document.querySelector('cart-drawer, #cart-drawer');
      var section = drawer && drawer.closest('.shopify-section, [id^="shopify-section-"]');
      if (!section) return false;

      var id = section.id.replace('shopify-section-', '');
      return typeof cart.sections[id] === 'string';
    }

    /**
     * GoKwik renders its checkout button `disabled` in the section markup and re-enables it from a 700ms TRAILING
     * debounce on a <body> childList+subtree MutationObserver (snippets/gokwik.liquid). While `disabled` is on the
     * button its own CSS shows the spinner: `.gokwik-checkout button.disabled .addloadr#btn-loader{display:flex}`.
     * So every burst of DOM writes we emit postpones the re-enable, and a burst whose gaps stay under 700ms
     * postpones it forever — the button spins until the shopper reloads. Doing here exactly what GoKwik's own
     * debounce does means our renders no longer depend on winning that race.
     */
    function settleThirdPartyButtons() {
      try {
        document.querySelectorAll('.gokwik-checkout button, #gokwik-buy-now').forEach(function (button) {
          button.disabled = false;
          button.classList.remove('disabled');
          button.removeAttribute('disabled');
        });
      } catch (e) {}
    }

    /**
     * The quiet window GoKwik waits for may never come on this page: the product offer timer
     * (snippets/product-offer-timer.liquid) rewrites its digits with textContent every second — a childList mutation
     * each tick — so <body> is never still for long here, while the home page (no timer) settles easily. That is the
     * page-level difference behind "checkout loads forever on the product page only".
     *
     * So settle deterministically instead: once now (the immediate render) and once after 1600ms — past the theme's
     * 1250ms delayed drawer re-render (theme.js.liquid:1793), which installs a fresh server-rendered `disabled`
     * button. These writes are attribute-only, so they never feed the childList observer they are compensating for.
     */
    function settleCheckoutSoon() {
      settleThirdPartyButtons();
      clearTimeout(settleCheckoutSoon._t);
      clearTimeout(settleCheckoutSoon._t2);
      settleCheckoutSoon._t = setTimeout(settleThirdPartyButtons, 1600);
      // A slow connection can land the theme's section re-render later than 1600ms; catch that too.
      settleCheckoutSoon._t2 = setTimeout(settleThirdPartyButtons, 3500);
    }

    function notifyThemeCartUpdate(cart, opts) {
      opts = opts || {};
      if (!cart) return;

      syncHeaderCartCount(cart);

      var baseEvent = opts.baseEvent || 'line-item:change';
      var cartDetail = { cart: cart, baseEvent: baseEvent };

      if (cartCanRenderDrawer(cart)) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: cartDetail
        }));
      } else {
        // No usable section markup. `cart:refresh` makes the drawer re-fetch and re-render itself, which is the same
        // outcome `cart:change` would have produced — without handing the theme a payload that makes it throw.
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }

      if (baseEvent === 'variant:add') {
        document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: opts.items || [],
            cart: cart
          }
        }));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('line-item:change', {
          bubbles: true,
          detail: {
            quantity: opts.quantity != null ? opts.quantity : cart.item_count,
            cart: cart
          }
        }));
      }

      ['cart:updated', 'cart:update', 'cart-update', 'theme:cart:updated'].forEach(function (name) {
        document.documentElement.dispatchEvent(new CustomEvent(name, {
          bubbles: true,
          detail: { cart: cart }
        }));
      });

      // These used to be a requestAnimationFrame + a 100ms timer that both re-ran syncHeaderCartCount "in case a
      // stale cache briefly wins". Each one rewrote the cart badges, i.e. each one was another childList mutation
      // under <body>, landing inside the window GoKwik needs to stay quiet. One write is enough: the count comes
      // from the cart payload we were just handed, and nothing between here and the next paint can overwrite it.
      settleCheckoutSoon();
    }

    // ---- Read cart -> reflect into PDP -----------------------------------
    // init() runs up to six times while the custom elements finish defining, and the cart events
    // can fire in bursts. Callers share whatever read is already in flight instead of stacking
    // identical requests, whose responses can land out of order and overwrite newer state.
    var cartReadPromise = null;
    var lastCartData = null;

    function syncFromCart() {
      if (!root()) return Promise.resolve(null);
      var variantId = getTrackedVariantId();
      if (!variantId) return Promise.resolve(null);
      currentVariantId = variantId;

      if (cartReadPromise) return cartReadPromise;

      cartReadPromise = fetch('/cart.js', { cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          lastCartData = cart;
          applyCartState(cart);
          return cart;
        })
        .catch(function () { return null; })
        .then(function (cart) {
          cartReadPromise = null;
          return cart;
        });

      return cartReadPromise;
    }

    // ---- Write PDP qty -> cart (and refresh drawer) ----------------------
    function pushQtyToCart(qty) {
      var variantId = getTrackedVariantId();
      if (!variantId || qty < 1) return;
      currentVariantId = variantId;
      var input = getActiveQtyInput() || getMainQtyInput();
      var wrap = getQtyWrap(input);
      var stickyQty = ENABLE_STICKY_ATC && getStickyBar() ? getStickyBar().querySelector('[data-ab-sticky-qty]') : null;
      var inlineQty = getInlineAtc() ? getInlineAtc().querySelector('[data-ab-inline-qty]') : null;
      // The spinner lives inside the quantity selector itself (sticky/inline count spinner), so
      // nothing extra is rendered next to it.
      if (wrap) wrap.classList.add('ab-qty-loading');
      if (stickyQty) stickyQty.classList.add('ab-qty-loading');
      if (inlineQty) inlineQty.classList.add('ab-qty-loading');

      function finishCartResponse(cart, resolvedQty, hadItem) {
        var resolvedItemQty = resolvedQty;
        var item = findCartItem(cart, getVariantIdCandidates());
        if (item) resolvedItemQty = item.quantity;

        // Keep cross-page cache in sync (same key as applyCartState and ab-product-card.js).
        if (cart && Array.isArray(cart.items)) {
          try {
            sessionStorage.setItem('ab_pc_cart', JSON.stringify({
              items: cart.items.map(function (it) {
                return { product_id: it.product_id, variant_id: it.variant_id, quantity: it.quantity };
              }),
              item_count: cart.item_count || 0
            }));
          } catch (e) {}
        }

        isInCart = !!(item || resolvedQty > 0);
        updateButtonStates(isInCart);
        setPdpQty(resolvedItemQty);

        // Render the drawer ONCE. This used to paint it twice — applyCartSections() swapped the markup here, and then
        // notifyThemeCartUpdate() dispatched `cart:change`, which makes the theme's CartDrawer._onCartChanged schedule
        // a SECOND innerHTML swap 1250ms later from its own snapshot (theme.js.liquid:1791-1793). Two swaps 1250ms
        // apart is both a stale-repaint risk and a second burst of <body> mutations inside GoKwik's quiet window.
        //
        // When we have section markup the theme's own handler is the one render we want, so we hand it the sections
        // and let it do the swap. Only when sections are missing do we fetch and swap the markup ourselves.
        if (cart && cart.sections) {
          notifyThemeCartUpdate(cart, {
            baseEvent: hadItem ? 'line-item:change' : 'variant:add',
            quantity: resolvedItemQty,
            items: hadItem ? [] : [{ id: parseInt(variantId, 10), quantity: resolvedItemQty }]
          });
          return;
        }

        refreshCartDrawerMarkup().then(function () {
          notifyThemeCartUpdate(cart, {
            baseEvent: hadItem ? 'line-item:change' : 'variant:add',
            quantity: resolvedItemQty,
            items: hadItem ? [] : [{ id: parseInt(variantId, 10), quantity: resolvedItemQty }]
          });
        });
      }

      function done() {
        if (wrap) wrap.classList.remove('ab-qty-loading');
        if (stickyQty) stickyQty.classList.remove('ab-qty-loading');
        if (inlineQty) inlineQty.classList.remove('ab-qty-loading');
        isSyncing = false;
        // Was called three times (now, next frame, and +80ms) to outlast whatever the theme might repaint. Each call
        // rewrites the add-to-cart / view-cart labels, so each one is another <body> childList mutation on the exact
        // timeline GoKwik's 700ms quiet-window debounce is trying to settle on. finishCartResponse has already set
        // the state from the authoritative cart; repeating it only added noise.
        updateButtonStates(isInCart);
      }

      isSyncing = true;
      lastQtyPushAt = Date.now();

      fetch('/cart.js', { cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var item = findCartItem(cart, getVariantIdCandidates());

          // A quantity interaction may only change a line that is already in the cart. It must never add one.
          //
          // The quantity stepper is shown in place of add to cart while the product IS in the cart, so reaching this
          // with no line means the shopper just removed it. Adding it back here is what made a removal bounce back
          // and need two or three attempts: removing fires the sticky bar's minus, that same click is also caught by
          // the capture-phase quantity listener, and 150ms later this ran and re-added the product it had removed.
          // Whether it lost that race depended on how fast the removal returned, which is why it was intermittent.
          if (!item) {
            applyCartState(cart, { force: true });
            return null;
          }

          var hadItem = true;
          var sectionIds = getCartSectionIds();
          var body = { id: item.key, quantity: qty };

          if (sectionIds.length) body.sections = sectionIds.join(',');

          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(body)
          }).then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw data;
              var sections = data.sections;
              return fetch('/cart.js', { cache: 'no-store' })
                .then(function (r) { return r.json(); })
                .then(function (fullCart) {
                  if (sections) fullCart.sections = sections;
                  fullCart._hadItem = hadItem;
                  return fullCart;
                });
            });
          });
        })
        .then(function (cart) {
          if (!cart) {
            done();
            return;
          }

          finishCartResponse(cart, qty, cart._hadItem);
          done();
        })
        .catch(function () {
          done();
          syncFromCart();
        });
    }

    function handlePdpQtyInteraction(sourceInput) {
      if (isSyncing) return;
      var input = (sourceInput && sourceInput.name === 'quantity') ? sourceInput : getActiveQtyInput();
      if (!input || !root() || !root().contains(input)) return;
      if (input.closest('product-quick-add')) return;
      if (input.closest('cart-drawer, #cart-drawer, .cart-drawer')) return;

      var q = parseInt(input.value, 10);
      if (isNaN(q) || q < 1) return;

      pushQtyToCart(q);
    }

    // ---- PDP quantity selector bindings ----------------------------------
    function bindQuantitySelector() {
      var el = root();
      if (!el || el.dataset.abQtyBound === 'true') return;
      el.dataset.abQtyBound = 'true';

      function isPdpQtyInput(input) {
        if (!input || input.name !== 'quantity') return false;
        if (!root() || !root().contains(input)) return false;
        if (input.closest('product-quick-add')) return false;
        if (input.closest('cart-drawer, #cart-drawer, .cart-drawer')) return false;
        return true;
      }

      function resolveQtyInput(target) {
        if (!target || !root() || !root().contains(target)) return null;
        if (target.name === 'quantity' && isPdpQtyInput(target)) return target;
        var host = target.closest('quantity-selector, .quantity-selector, .quantity, quantity-input, [data-ab-sticky-qty], [data-ab-inline-qty], .product-info__quantity-selector');
        if (!host || !root().contains(host)) return null;
        var input = host.querySelector ? host.querySelector('input[name="quantity"]') : null;
        return isPdpQtyInput(input) ? input : null;
      }

      var t;
      function schedulePdpQtySync(sourceInput) {
        clearTimeout(t);
        t = setTimeout(function () {
          handlePdpQtyInteraction(sourceInput);
          updateButtonStates(isInCart);
        }, 150);
      }

      // Capture phase so theme handlers cannot block cart sync.
      el.addEventListener('click', function (e) {
        var qtyInput = resolveQtyInput(e.target);
        if (!qtyInput) return;
        var wrap = getQtyWrap(qtyInput);
        if (!wrap || !wrap.contains(e.target)) return;
        // Show spinner immediately (before theme increments the value) to prevent number flash
        if (ENABLE_STICKY_ATC && !isSyncing) {
          var stickyQtyEl = getStickyBar() ? getStickyBar().querySelector('[data-ab-sticky-qty]') : null;
          if (stickyQtyEl && stickyQtyEl.contains(e.target)) {
            stickyQtyEl.classList.add('ab-qty-loading');
          }
        }
        schedulePdpQtySync(qtyInput);
      }, true);

      el.addEventListener('change', function (e) {
        var qtyInput = resolveQtyInput(e.target);
        if (!qtyInput) return;
        schedulePdpQtySync(qtyInput);
      }, true);

      el.addEventListener('input', function (e) {
        var qtyInput = resolveQtyInput(e.target);
        if (!qtyInput) return;
        schedulePdpQtySync(qtyInput);
      }, true);
    }

    // ---- Cart drawer <-> sticky qty sync (sticky bar only) ---------------
    function bindGlobalCartQtySync() {
      if (!ENABLE_STICKY_ATC || !getStickyBar()) return;
      var boundKey = '_abStickyCartQtyBound_' + SECTION_ID;
      if (window[boundKey]) return;
      window[boundKey] = true;
      var debounce;

      function scheduleSync() {
        if (isSyncing || Date.now() - lastQtyPushAt < 600 || !ENABLE_STICKY_ATC || !getStickyBar() || !root()) return;
        clearTimeout(debounce);
        debounce = setTimeout(syncFromCart, 150);
      }

      document.addEventListener('click', function (e) {
        if (!ENABLE_STICKY_ATC || !getStickyBar() || !root()) return;
        var drawer = e.target.closest('cart-drawer, #cart-drawer, .cart-drawer');
        if (!drawer) return;
        if (!e.target.closest('quantity-selector, .quantity-selector, .line-item__quantity')) return;
        if (!e.target.closest('button')) return;
        scheduleSync();
      });

      document.addEventListener('change', function (e) {
        if (!ENABLE_STICKY_ATC || !getStickyBar() || !root()) return;
        if (!e.target.closest('cart-drawer, #cart-drawer, .cart-drawer')) return;
        var name = e.target.getAttribute('name') || '';
        if (e.target.name === 'quantity' || name.indexOf('updates') === 0) {
          scheduleSync();
        }
      });

      ['cart:updated', 'cart:change', 'cart:refresh', 'line-item:quantity-change'].forEach(function (ev) {
        document.addEventListener(ev, scheduleSync);
      });
    }

    /**
     * /cart/change.js, /cart/update.js and /cart.js answer with the whole cart. /cart/add.js does not: given an items
     * array it answers with only the lines it just added. Treating that as the cart wiped every other line out of our
     * state, so a bundle add that did not include this product flipped it to "not in cart" while it sat in the cart —
     * which showed the wrong button, left the loader spinning, and made the next quantity interaction re-add it.
     * item_count is present on every real cart payload and absent from an add response.
     */
    function isFullCart(data) {
      return !!data && Array.isArray(data.items) && typeof data.item_count === 'number';
    }

    function handleCartMutationResponse(response) {
      if (!response || !response.ok || !root()) return;
      // Any cart mutation — ours, the theme's drawer, an app's — ends with the drawer footer re-rendered around a
      // server-side `disabled` GoKwik button. Settle it here so recovery never depends on the quiet window.
      settleCheckoutSoon();
      response.clone().json().then(function (data) {
        if (isFullCart(data)) {
          applyCartState(data);
        } else {
          syncFromCart();
        }
      }).catch(function () {
        syncFromCart();
      });
    }

    // ---- Detect cart mutations from ANYWHERE (drawer, header, apps) ------
    function isCartMutationUrl(url) {
      return !!url && (
        url.indexOf('/cart/add') !== -1 ||
        url.indexOf('/cart/change') !== -1 ||
        url.indexOf('/cart/update') !== -1 ||
        url.indexOf('/cart/clear') !== -1
      );
    }

    if (!window._abCartFetchPatched) {
      window._abCartFetchPatched = true;
      var _fetch = window.fetch;
      window.fetch = function (input) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var p = _fetch.apply(this, arguments);
        if (isCartMutationUrl(url)) {
          p.then(function (r) {
            if (isSyncing || !root()) return;
            handleCartMutationResponse(r);
          }).catch(function () {});
        }
        return p;
      };
    }

    if (!window._abCartXhrPatched) {
      window._abCartXhrPatched = true;
      var _open = XMLHttpRequest.prototype.open;
      var _send = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        this._abUrl = url;
        return _open.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function () {
        var xhr = this;
        if (isCartMutationUrl(xhr._abUrl)) {
          xhr.addEventListener('load', function () {
            if (isSyncing || !root()) return;
            if (xhr.status >= 200 && xhr.status < 300) {
              settleCheckoutSoon();
              try {
                var data = JSON.parse(xhr.responseText);
                if (isFullCart(data)) {
                  applyCartState(data);
                } else {
                  syncFromCart();
                }
              } catch (err) {
                syncFromCart();
              }
            }
          });
        }
        return _send.apply(this, arguments);
      };
    }

    // Idempotent: opens the drawer if closed, never toggles. Toggling here was
    // what caused the "click many times to open" bug — a click on the (outside)
    // View Cart button both reached this opener and the theme's focus-trap,
    // which closed the already-open drawer.
    function openCartDrawer() {
      // Full cart drawer only — never the small <cart-notification-drawer> popover.
      var drawer = document.getElementById('cart-drawer') ||
        document.querySelector('cart-drawer, mini-cart, sticky-cart, ajax-cart, side-cart');
      if (drawer) {
        if (drawer.hasAttribute('open') || drawer.classList.contains('is-open')) return;
        if (typeof drawer.show === 'function') { drawer.show(); return; }
        drawer.removeAttribute('hidden');
        drawer.removeAttribute('inert');
        drawer.setAttribute('open', '');
        drawer.classList.add('is-open', 'open', 'active');
        return;
      }
      var headerCartBtn = (
        document.querySelector('#cart-icon-bubble') ||
        document.querySelector('[data-cart-toggle]') ||
        document.querySelector('[data-open="cart-drawer"]') ||
        document.querySelector('button[aria-controls="CartDrawer"]') ||
        document.querySelector('button[aria-controls="cart-drawer"]') ||
        document.querySelector('button[aria-controls="cart-notification"]') ||
        document.querySelector('.header__icon--cart') ||
        document.querySelector('a[href="/cart"]:not([data-ab-sticky-view-cart])')
      );
      if (headerCartBtn) { headerCartBtn.click(); return; }
      window.location.href = '/cart';
    }

    // Manual-open only. Only cartType "drawer" auto-opens the full <cart-drawer>
    // on add; we suppress that via `detail.blockCartDrawerOpening` in the CAPTURE
    // phase — but ONLY for "drawer", because in "popover" mode the same event
    // drives the theme's add-to-cart popup and the flag would suppress it.
    // Shares its guard flag with ab-product-card.js (registered once per page).
    if (!window.__abBlockCartAutoOpen) {
      window.__abBlockCartAutoOpen = true;
      document.addEventListener('variant:add', function (e) {
        var s = window.themeVariables && window.themeVariables.settings;
        if (s && s.cartType === 'drawer' && e.detail) e.detail.blockCartDrawerOpening = true;
      }, true);
    }

    // CAPTURE-phase handler so we run before the theme's focus-trap document
    // listener, which would otherwise read a click on the (outside) sticky
    // View Cart button as an "outside click" and close an already-open drawer,
    // forcing the user to click again. openCartDrawer() is idempotent.
    if (!window._abStickyViewCartCaptureBound) {
      window._abStickyViewCartCaptureBound = true;
      document.addEventListener('click', function (e) {
        var vcBtn = e.target.closest('[data-ab-sticky-view-cart]');
        if (!vcBtn) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        openCartDrawer();
      }, true);
    }

    function wireStickyViewCartButton() {
      if (!ENABLE_STICKY_ATC) return;
      var bar = getStickyBar();
      if (!bar) return;
      var bottomAtc = bar.querySelector('[data-ab-sticky-atc]');
      if (!bottomAtc || bottomAtc.dataset.abStickyWired === 'true') return;
      if (bar.querySelector('[data-ab-sticky-view-cart]')) return;

      bottomAtc.dataset.abStickyWired = 'true';

      var viewCartBtn = document.createElement('button');
      viewCartBtn.type = 'button';
      viewCartBtn.setAttribute('data-ab-sticky-view-cart', '');
      viewCartBtn.innerHTML = '<span style="position:relative;display:inline-flex;align-items:center;margin-right:6px;flex-shrink:0;"><img src="https://www.vilvahstore.com/cdn/shop/t/205/assets/bag_animation.gif?&width=22" width="22" height="22" alt="" aria-hidden="true" style="vertical-align:middle;display:block;"><span data-ab-sticky-bag-count data-count="0" aria-hidden="true"></span></span>' + VIEW_CART_LABEL + '<span aria-hidden="true" style="margin-left:6px;display:inline-flex;align-items:center;"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 32 32" fill="currentColor"><path d="M 18.71875 6.78125 L 17.28125 8.21875 L 24.0625 15 L 4 15 L 4 17 L 24.0625 17 L 17.28125 23.78125 L 18.71875 25.21875 L 27.21875 16.71875 L 27.90625 16 L 27.21875 15.28125 Z"/></svg></span>';
      viewCartBtn.className = 'ab-vc-hidden';
      viewCartBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openCartDrawer();
      });
      viewCartBtn.dataset.vcWired = 'true';
      bottomAtc.parentNode.insertBefore(viewCartBtn, bottomAtc.nextSibling);
    }

    // On variant change: track new ID and re-sync so the right qty state shows
    document.addEventListener('variant:change', function (e) {
      if (!root()) return;
      var variantId = (e.detail && e.detail.variant && e.detail.variant.id) || getCurrentVariantId();
      if (variantId) {
        currentVariantId = variantId;
        syncFromCart();
      }
      updateBottomStickyBar(e.detail && e.detail.variant);
    });

    // ---- Bottom sticky add to cart bar -----------------------------------
    function getStickyVariants() {
      var node = document.getElementById('ab-sticky-variants-' + SECTION_ID);
      if (!node) return [];
      try { return JSON.parse(node.textContent); } catch (err) { return []; }
    }

    function formatMoney(cents) {
      var formatted;
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        formatted = Shopify.formatMoney(cents);
      } else {
        var priceEl = document.querySelector('[data-ab-sticky-price]');
        var sym = priceEl ? (priceEl.textContent.trim().match(/^[^\d\s]+/) || [''])[0] : '₹';
        formatted = sym + (cents / 100).toFixed(2);
      }
      return formatted.replace(/[.,]00$/, '');
    }

    function findMainVariantInput() {
      var el = root();
      if (!el) return null;
      var input = el.querySelector('product-form input[name="id"]');
      if (input) return input;
      var forms = el.querySelectorAll('form[id^="product-form"], form[is="product-form"]');
      for (var i = 0; i < forms.length; i++) {
        input = forms[i].querySelector('input[name="id"]');
        if (input) return input;
      }
      return null;
    }

    function updateBottomStickyBar(variant) {
      if (!variant) {
        var variants = getStickyVariants();
        var variantId = currentVariantId || getCurrentVariantId();
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === String(variantId)) {
            variant = variants[i];
            break;
          }
        }
      }
      if (!variant) return;

      var bar = getStickyBar();
      if (bar) {
        var select = bar.querySelector('[data-ab-sticky-variant-select]');
        if (select && String(select.value) !== String(variant.id)) {
          select.value = variant.id;
        }

        var picker = bar.querySelector('[data-ab-sticky-variant-picker]');
        if (picker) {
          var label = picker.querySelector('[data-ab-sticky-variant-label]');
          if (label) {
            var optionText = '';
            if (select) {
              var activeOption = select.options[select.selectedIndex];
              if (activeOption) optionText = activeOption.textContent.trim();
            }
            if (!optionText && variant.title) optionText = variant.title;
            if (optionText) label.textContent = optionText;
          }
          picker.querySelectorAll('[data-ab-sticky-variant-option]').forEach(function (opt) {
            var isSelected = String(opt.getAttribute('data-variant-id')) === String(variant.id);
            opt.classList.toggle('is-selected', isSelected);
            opt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          });
        }

        var priceEl = bar.querySelector('[data-ab-sticky-price]');
        if (priceEl && variant.price != null) {
          priceEl.textContent = formatMoney(variant.price);
        }

        var mediaEl = bar.querySelector('[data-ab-sticky-media]');
        if (mediaEl && variant.featured_media && variant.featured_media.preview_image) {
          mediaEl.src = variant.featured_media.preview_image.src;
        }

        var atcBtn = bar.querySelector('[data-ab-sticky-atc]');
        if (atcBtn) {
          atcBtn.disabled = !variant.available;
          var textEl = atcBtn.querySelector('.button__text');
          if (textEl) {
            textEl.textContent = variant.available
              ? (atcBtn.getAttribute('data-atc-label') || 'Add to Cart')
              : (atcBtn.getAttribute('data-soldout-label') || 'Sold Out');
          }
        }
      }

      updateInlineAtcVariant(variant);
    }

    function updateInlineAtcVariant(variant) {
      var wrap = getInlineAtc();
      if (!wrap || !variant) return;
      var atcBtn = wrap.querySelector('[data-ab-inline-atc-btn]');
      if (!atcBtn) return;
      atcBtn.disabled = !variant.available;
      var textEl = atcBtn.querySelector('.button__text');
      if (textEl) {
        textEl.textContent = variant.available
          ? (atcBtn.getAttribute('data-atc-label') || 'Add to Cart')
          : (atcBtn.getAttribute('data-soldout-label') || 'Sold Out');
      }
    }

    function closeStickyVariantMenu(picker) {
      if (!picker) return;
      picker.classList.remove('is-open');
      var trigger = picker.querySelector('[data-ab-sticky-variant-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    function openStickyVariantMenu(picker) {
      if (!picker) return;
      picker.classList.add('is-open');
      var trigger = picker.querySelector('[data-ab-sticky-variant-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    function bindStickyVariantPicker(bar) {
      var picker = bar.querySelector('[data-ab-sticky-variant-picker]');
      var select = bar.querySelector('[data-ab-sticky-variant-select]');
      if (!picker || !select || picker.dataset.abVariantBound === 'true') return;
      picker.dataset.abVariantBound = 'true';

      var trigger = picker.querySelector('[data-ab-sticky-variant-trigger]');
      var menu = picker.querySelector('[data-ab-sticky-variant-menu]');

      function applyVariantSelection(variantId) {
        if (!variantId || String(select.value) === String(variantId)) return;
        select.value = variantId;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (trigger) {
        trigger.addEventListener('click', function (e) {
          e.stopPropagation();
          if (picker.classList.contains('is-open')) {
            closeStickyVariantMenu(picker);
          } else {
            openStickyVariantMenu(picker);
          }
        });
      }

      if (menu) {
        menu.querySelectorAll('[data-ab-sticky-variant-option]').forEach(function (option) {
          option.addEventListener('click', function (e) {
            e.stopPropagation();
            if (option.classList.contains('is-disabled')) return;
            applyVariantSelection(option.getAttribute('data-variant-id'));
            closeStickyVariantMenu(picker);
          });
        });
      }

      if (!window._abStickyVariantOutsideBound) {
        window._abStickyVariantOutsideBound = true;
        document.addEventListener('click', function (e) {
          document.querySelectorAll('[data-ab-sticky-variant-picker].is-open').forEach(function (openPicker) {
            if (!openPicker.contains(e.target)) {
              closeStickyVariantMenu(openPicker);
            }
          });
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape') {
            document.querySelectorAll('[data-ab-sticky-variant-picker].is-open').forEach(closeStickyVariantMenu);
          }
        });
      }
    }

    function removeStickyItemFromCart() {
      var variantId = getTrackedVariantId();
      if (!variantId) return;
      var bar = getStickyBar();
      var qtyWrap = bar ? bar.querySelector('[data-ab-sticky-qty]') : null;
      var inlineQty = getInlineAtc() ? getInlineAtc().querySelector('[data-ab-inline-qty]') : null;
      if (qtyWrap) qtyWrap.classList.add('ab-qty-loading');
      if (inlineQty) inlineQty.classList.add('ab-qty-loading');
      isSyncing = true;
      lastQtyPushAt = Date.now();
      fetch('/cart.js', { cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var item = findCartItem(cart, getVariantIdCandidates());
          if (!item) {
            applyCartState(cart, { force: true });
            return Promise.resolve(null);
          }
          var sectionIds = getCartSectionIds();
          var body = { id: item.key, quantity: 0 };
          if (sectionIds.length) body.sections = sectionIds.join(',');
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(body)
          }).then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw data;
              return fetch('/cart.js', { cache: 'no-store' })
                .then(function (r) { return r.json(); })
                .then(function (fullCart) {
                  if (data.sections) fullCart.sections = data.sections;
                  return fullCart;
                });
            });
          });
        })
        .then(function (cart) {
          if (qtyWrap) qtyWrap.classList.remove('ab-qty-loading');
          if (inlineQty) inlineQty.classList.remove('ab-qty-loading');
          isSyncing = false;
          if (!cart) return;
          // Single render: notifyThemeCartUpdate hands the sections to the theme's own cart:change handler (or falls
          // back to cart:refresh). Swapping the markup here too made the drawer paint twice 1250ms apart.
          notifyThemeCartUpdate(cart, { baseEvent: 'line-item:change', quantity: 0 });
          applyCartState(cart, { force: true });
        })
        .catch(function () {
          if (qtyWrap) qtyWrap.classList.remove('ab-qty-loading');
          if (inlineQty) inlineQty.classList.remove('ab-qty-loading');
          isSyncing = false;
          syncFromCart();
        });
    }

    function bindInlineAtc() {
      var wrap = getInlineAtc();
      if (!wrap || wrap.dataset.abBound === 'true') return;
      wrap.dataset.abBound = 'true';

      var atcBtn = wrap.querySelector('[data-ab-inline-atc-btn]');
      if (atcBtn) {
        atcBtn.addEventListener('click', function () {
          if (atcBtn.disabled || atcBtn.classList.contains('is-loading')) return;
          setAtcLoading(true);
        });
      }

      var decreaseBtn = wrap.querySelector('[data-ab-inline-decrease-btn]');
      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', function () {
          var qtyWrap = wrap.querySelector('[data-ab-inline-qty]');
          if (!qtyWrap || !qtyWrap.hasAttribute('data-qty-is-one')) return;
          setTimeout(function () {
            var inp = getInlineQtyInput();
            if (inp && parseInt(inp.value, 10) < 1) inp.value = '1';
          }, 0);
          removeStickyItemFromCart();
        });
      }
    }

    function bindBottomStickyBar() {
      var bar = getStickyBar();
      if (!bar || bar.dataset.abBound === 'true') return;
      bar.dataset.abBound = 'true';

      bindStickyVariantPicker(bar);

      var atcBtn = bar.querySelector('[data-ab-sticky-atc]');
      if (atcBtn) {
        atcBtn.addEventListener('click', function () {
          if (atcBtn.disabled || atcBtn.classList.contains('is-loading')) return;
          setAtcLoading(true);
        });
      }

      var viewCartBtn = bar.querySelector('[data-ab-sticky-view-cart]');
      if (viewCartBtn && !viewCartBtn.dataset.vcWired) {
        viewCartBtn.dataset.vcWired = 'true';
        viewCartBtn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          openCartDrawer();
        });
      }

      var decreaseBtn = bar.querySelector('[data-ab-sticky-decrease-btn]');
      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', function () {
          var qtyWrap = bar.querySelector('[data-ab-sticky-qty]');
          if (!qtyWrap || !qtyWrap.hasAttribute('data-qty-is-one')) return;
          // Reset input so the web component doesn't leave it at 0
          setTimeout(function () {
            var inp = getStickyQtyInput();
            if (inp && parseInt(inp.value, 10) < 1) inp.value = '1';
          }, 0);
          removeStickyItemFromCart();
        });
      }

      var select = bar.querySelector('[data-ab-sticky-variant-select]');
      if (select) {
        select.addEventListener('change', function () {
          var variantId = select.value;
          var input = findMainVariantInput();
          if (input && String(input.value) !== String(variantId)) {
            input.value = variantId;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            input.dispatchEvent(new Event('input', { bubbles: true }));
          }
          currentVariantId = parseInt(variantId, 10);
          updateBottomStickyBar();
          syncFromCart();
        });
      }
    }

    function bindAtcLoadingSync() {
      var el = root();
      if (!el || el.dataset.abAtcLoadBound === 'true') return;
      el.dataset.abAtcLoadBound = 'true';

      var formId = 'product-form-' + PRODUCT_ID + '-' + SECTION_ID;

      el.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || form.id !== formId) return;
        setAtcLoading(true);
      }, true);

      // Theme product-form add success / failure
      // The cart has demonstrably changed by the time either of these fires, so the loader comes down either way.
      // Gating it on isInCart meant one wrong reading of the cart left the button spinning until the 10s safety.
      document.addEventListener('variant:add', function () {
        if (!root()) return;
        setTimeout(function () { setAtcLoading(false); }, 50);
      });

      document.addEventListener('cart:change', function () {
        if (!root()) return;
        setTimeout(function () { setAtcLoading(false); }, 50);
      });

      // Safety: never leave loaders stuck
      document.addEventListener('product:added', function () {
        if (!root()) return;
        setAtcLoading(false);
      });
    }

    var initialCartRead = false;

    function init() {
      var el = root();
      if (!el) return;

      bindBottomStickyBar();
      bindInlineAtc();
      bindAtcLoadingSync();
      updateBottomStickyBar();
      wireStickyViewCartButton();

      if (ENABLE_STICKY_ATC) {
        bindGlobalCartQtySync();
      }

      currentVariantId = currentVariantId || getCurrentVariantId();
      updateButtonStates(isInCart);

      // Read the cart once, not once per init() retry. The flag flips only on a successful read,
      // so a failed request is retried by the next attempt. Later retries exist for elements that
      // finished defining themselves late, so they replay the cart already in memory instead: same
      // state, no network.
      if (!initialCartRead) {
        syncFromCart().then(function (cart) {
          if (cart) initialCartRead = true;
        });
      } else if (lastCartData) {
        applyCartState(lastCartData);
      }

      bindQuantitySelector();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }

    if (window.customElements && customElements.whenDefined) {
      ['buy-buttons', 'product-form', 'cart-drawer'].forEach(function (n) {
        customElements.whenDefined(n).then(init).catch(function () {});
      });
    }

    setTimeout(init, 400);
    setTimeout(init, 1200);

    window.addEventListener('pageshow', function (e) {
      if (e.persisted) syncFromCart();
    });
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('script[data-ab-cart-sync]');

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];

      if (node.getAttribute('data-ab-booted') === 'true') continue;

      node.setAttribute('data-ab-booted', 'true');

      try {
        boot(JSON.parse(node.textContent));
      } catch (error) {
        // A malformed config block must not take the rest of the page down with it.
      }
    }
  }

  initAll();

  // The theme editor swaps the section's markup without reloading the page.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
